<template>
  <div class="hc-image-upload">
    <el-upload
      v-if="!modelValue"
      class="avatar-uploader"
      :style="{ width: width + 'px', height: height + 'px' }"
      :action="uploadUrl"
      :headers="uploadHeaders"
      :show-file-list="false"
      :on-success="handleUploadSuccess"
      :before-upload="beforeUpload"
      :accept="accept"
    >
      <el-icon class="avatar-uploader-icon" :style="{ width: width + 'px', height: height + 'px', lineHeight: height + 'px' }">
        <Plus />
      </el-icon>
    </el-upload>
    <div v-else class="avatar-preview-wrapper" :style="{ width: width + 'px', height: height + 'px' }">
      <img :src="modelValue" referrerpolicy="no-referrer" class="avatar" />
      <div class="avatar-actions">
        <span class="action-btn" @click="handlePreview(modelValue)">
          <el-icon><ZoomIn /></el-icon>
        </span>
        <span class="action-btn" @click="handleRemove">
          <el-icon><Delete /></el-icon>
        </span>
      </div>
    </div>

    <!-- 大图展示弹窗 -->
    <el-dialog v-model="previewVisible" title="图片预览" width="600px" align-center append-to-body>
      <img :src="previewImageUrl" referrerpolicy="no-referrer" style="width: 100%; max-height: 500px; object-fit: contain; display: block; margin: 0 auto;" />
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { ElMessage } from "element-plus";
import { Plus, ZoomIn, Delete } from "@element-plus/icons-vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: ""
  },
  width: {
    type: Number,
    default: 100
  },
  height: {
    type: Number,
    default: 100
  },
  sizeLimit: {
    type: Number,
    default: 10 // 单位: MB
  },
  accept: {
    type: String,
    default: "image/jpeg,image/png,image/gif,image/webp"
  }
});

const emit = defineEmits(["update:modelValue", "success", "remove"]);

const previewVisible = ref(false);
const previewImageUrl = ref("");

const uploadUrl = computed(() => {
  return (window.configs?.BASE_URL || "") + "/file/imges";
});

const uploadHeaders = computed(() => {
  const userInfo = localStorage.getItem("user");
  if (userInfo) {
    try {
      const tokenObj = JSON.parse(userInfo);
      return {
        Authorization: tokenObj?.user?.token || ""
      };
    } catch (e) {
      return {};
    }
  }
  return {};
});

const beforeUpload = (rawFile) => {
  // 简单验证图片格式
  const fileType = rawFile.type;
  const acceptTypes = props.accept.split(",");
  const isTypeValid = acceptTypes.includes(fileType);
  
  if (!isTypeValid && props.accept) {
    ElMessage.error("上传图片格式不正确!");
    return false;
  }
  if (rawFile.size / 1024 / 1024 > props.sizeLimit) {
    ElMessage.error(`上传图片大小不能超过 ${props.sizeLimit}MB!`);
    return false;
  }
  return true;
};

const handleUploadSuccess = (response) => {
  if (response.code === 200) {
    let finalUrl = response.data || "";
    // 如果后端返回的是相对路径，并且配置了 BASE_URL，自动拼装为绝对路径方便预览展示
    if (finalUrl && !finalUrl.startsWith("http://") && !finalUrl.startsWith("https://")) {
      const baseUrl = window.configs?.BASE_URL || "";
      // 去除 baseUrl 尾部多余的斜杠，以及 finalUrl 头部多余的斜杠，确保拼接格式正确
      const cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
      const cleanPath = finalUrl.startsWith("/") ? finalUrl : "/" + finalUrl;
      finalUrl = cleanBase + cleanPath;
    }
    emit("update:modelValue", finalUrl);
    emit("success", finalUrl);
    ElMessage.success("上传成功");
  } else {
    ElMessage.error(response.msg || "上传失败");
  }
};

const handlePreview = (url) => {
  previewImageUrl.value = url;
  previewVisible.value = true;
};

const handleRemove = () => {
  emit("update:modelValue", "");
  emit("remove");
};
</script>

<style scoped>
.hc-image-upload {
  display: inline-block;
}

.avatar-uploader {
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar-uploader:hover {
  border-color: var(--el-color-primary);
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  text-align: center;
}

.avatar-preview-wrapper {
  position: relative;
  border-radius: 6px;
  border: 1px dashed var(--el-border-color);
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.avatar-preview-wrapper .avatar {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.avatar-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  opacity: 0;
  transition: opacity 0.3s;
}

.avatar-preview-wrapper:hover .avatar-actions {
  opacity: 1;
}

.avatar-actions .action-btn {
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.avatar-actions .action-btn:hover {
  color: var(--el-color-primary);
}
</style>
